import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { NotificationsService } from 'angular2-notifications';
import { AnyKindOfDictionary } from 'lodash';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { AutocompleteObjectValidator } from 'src/app/core/validators/autocomplete-object.validator';
import { LoaderService } from 'src/app/core/_config/loader.service';
import { YesOrNoComponent } from 'src/app/dialog/yes-or-no/yes-or-no.component';
import { CashierManagementService } from '../../services/cashier-management.service';

@Component({
    selector: 'kt-cashier-management-transfer-batch',
    templateUrl: './cashier-management-transfer-batch.component.html',
    styleUrls: ['./cashier-management-transfer-batch.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class CashierManagementTransferBatchComponent implements OnInit {
    public cashierList: Array<any>;
    private entityIdSelected: string;
    private batchIdSelected: string;

    public cashierControl = new FormControl('', { validators: [AutocompleteObjectValidator(), Validators.required] });
    public filteredCashiers: Observable<any[]>;

    constructor(
        public dialogRef: MatDialogRef<CashierManagementTransferBatchComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private _dialog: MatDialog,
        private _spinner: NgxSpinnerService,
        public _loaderService: LoaderService,
        private _cashierManagementService: CashierManagementService,
        private _translate: TranslateService,
        private _notificationsService: NotificationsService,
    ) {
        this.cashierList = new Array<any>();
        this.entityIdSelected = data.cashier.entityId;
        this.batchIdSelected = data.cashier.batchId;
        this.filteredCashiers = new Observable<any[]>();
    }

    ngOnInit(): void {
        this.filteredCashiers = this.cashierControl.valueChanges.pipe(
            startWith(''),
            map((cashierName) => (cashierName ? this._filter(cashierName) : this.cashierList.slice())),
        );

        this.getCashierList();
    }

    private getCashierList(): void {
        this._spinner.show();
        this._cashierManagementService.getCashiersToTransferByEntityId(this.entityIdSelected).subscribe((result) => {
            if (result.success) {
                this.cashierList = result.data;
            }
            this._spinner.hide();
        });
    }

    private _filter(value: any): string[] {
        let filterValue = '';
        if (value === '') {
            return this.cashierList.slice();
        }

        if (typeof value !== 'string') {
            filterValue = value.cashierName.toLowerCase();
        } else {
            filterValue = value.toLowerCase();
        }

        return this.cashierList.filter((option) => option.cashierName.toLowerCase().includes(filterValue));
    }

    public displayCashierFn(cashier?: any): string | undefined {
        return cashier ? cashier.cashierName : undefined;
    }

    private yesOrNoDialog(title: string, text: string): Observable<any> {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = true;
        dialogConfig.minWidth = 260;
        dialogConfig.data = {
            title: title,
            text: text,
        };
        const dialogRef = this._dialog.open(YesOrNoComponent, dialogConfig);

        return dialogRef.afterClosed();
    }

    public onCancelTransfer(): void {
        this.dialogRef.close();
    }

    public onTransfer(): void {
        if (this.cashierControl.valid) {
            this.yesOrNoDialog(
                this._translate.instant('CASHIERMANAGEMENT.transfer_of_cashier'),
                this._translate.instant(
                    'CASHIERMANAGEMENT.are_you_sure_you_want_to_transfer_cashier_to_this_selected_cashier',
                ),
            ).subscribe((result) => {
                if (result) {
                    this._spinner.show();
                    this._cashierManagementService
                        .transferBatchToCashier(this.batchIdSelected, this.cashierControl.value.id)
                        .subscribe((result) => {
                            if (result.success) {
                                this._notificationsService.success(
                                    this._translate.instant('CASHIERMANAGEMENT.transfer_of_cashier'),
                                    this._translate.instant('CASHIERMANAGEMENT.cashier_transferred'),
                                );
                                this.dialogRef.close(true);
                            }
                            this._spinner.hide();
                        });
                }
            });
        } else {
            this._notificationsService.alert(
                this._translate.instant('CASHIERMANAGEMENT.transfer_of_cashier'),
                this._translate.instant('CASHIERMANAGEMENT.please_select_cashier'),
            );
        }
    }
}
